import React, { FC, useState, useEffect } from 'react';

import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { ProductMicrodata } from 'components/PageSpecificMetadata/Microdata/ProductMicrodata';
import { PhotoSwipe } from 'components_common/customized_modules/react-photoswipe/PhotoSwipe';
import { getDefaultOptions, transformImagesToPhotoswipe } from 'helpers/photoswipe';
import { getImageAlt } from 'helpers/seoHelper';
import { formatPrice } from 'helpers/stringFormatters';
import { useUrlBuilder } from 'hooks/useUrlBuilder';
import { useAppContext } from 'store/appContext';

import { getAddToCart } from './logic/getAddToCart';
import { getBreadcrumbs } from './logic/getBreadcrumbs';
import { getFormattedModelNumber } from './logic/getFormattedModelNumber';
import { getImageControls } from './logic/getImageControls';
import { getSelector } from './logic/getSelector';
import { getSizeOptionsData } from './logic/getSizeOptionsData';
import { isProductNotFound } from './logic/isProductNotFound';
import { trackPdpVisited } from './logic/trackPdpVisited';
import { ImageSwipe } from './types/Imageswipe';
import { SelectedSize } from './types/SelectedSize';
import { HelpfulInformation } from './view/HelpfulInformation/HelpfulInformation';
import {
  breadcrumbsArea,
  helpfulInfoArea,
  Layout,
  mainImageArea,
  productActionCardArea,
  productDescriptionArea,
  relatedProductsArea,
  tabsArea,
  thumbnailsArea,
} from './view/Layout';
import { MainImage } from './view/MainImage/MainImage';
import { ModalProductExcluded } from './view/ModalProductExcluded/ModalProductExcluded';
import { ModalSizeGuide } from './view/ModalSizeGuide';
import { ProductActionCard } from './view/ProductActionCard/ProductActionCard';
import { getProductDescriptionData } from './view/ProductDescriptionData/getProductDescriptionData';
import { ProductDescription } from './view/ProductDescriptionData/ProductDescription';
import { ProductNotFound } from './view/ProductNotFound/ProductNotFound';
import { RelatedProducts } from './view/RelatedProducts/RelatedProducts';
import { Tabs } from './view/Tabs';
import { Thumbnails } from './view/Thumbnails';

export const ProductDetails: FC = () => {
  const {
    country,
    countryCode,
    billingCurrencyCode,
    currencySymbol,
    department,
    freeShipping,
    productDetails,
  } = useAppContext();
  const { isCountryDDP, product, relatedProducts, userFacingDepartment } = productDetails;
  const { brand, isExcluded, images, model, modelNumber, sizesList, sizeSystem, storeColor } = product;
  const { localized } = useUrlBuilder();

  const { selectorOptions, selectorTitleText } = getSelector({ sizesList, sizeSystem });
  const preselected = selectorOptions.length === 1 ? selectorOptions[0].value : null;
  const isOutOfStock = selectorOptions.length === 0;

  const [selectedSize, setSelectedSize] = useState<SelectedSize>({
    selected: preselected,
    selectedOption: selectorOptions.find(option => option.value === preselected),
    showSelectSizeBadge: false,
  });
  const [imageSwipe, setImageSwipe] = useState<ImageSwipe>({
    currentImageIndex: 0,
    isOpen: false,
    swipeOptions: getDefaultOptions(0),
  });
  const [sizeGuideVisible, setSizeGuideVisible] = useState(false);

  useEffect(() => {
    trackPdpVisited({ currencySymbol, department, isOutOfStock, productDetails });

    setImageSwipe({
      ...imageSwipe,
      images: transformImagesToPhotoswipe(images, true),
    });
  }, []);

  const { addToCart } = getAddToCart({ currencySymbol, localized, productDetails, selectedSize });
  const { breadcrumbList } = getBreadcrumbs({ localized, product, userFacingDepartment });
  const { displayProductImage, handleCarouselSlideChange, handlePhotoSwipeOpen } = getImageControls({
    imageSwipe,
    setImageSwipe,
  });
  const imagesAlt = getImageAlt({ brandName: brand.name, color: storeColor, model });
  const formattedModelNumber = getFormattedModelNumber(modelNumber);
  const { isLastOne, isSomeSizeLastOne, isUniversalSizeOnly } = getSizeOptionsData({ product, selectorOptions });
  const { productDescriptions, initialDesktopTab } = getProductDescriptionData({
    country,
    department,
    formattedModelNumber,
    isCountryDDP,
    isLastOne,
    isSomeSizeLastOne,
    product,
    setSizeGuideVisible,
  });
  const [currentDesktopTab, setCurrentDesktopTab] = useState(initialDesktopTab);

  if (isProductNotFound({ product })) {
    return <ProductNotFound countryCode={countryCode} freeShipping={freeShipping} product={product} />;
  }

  return (
    <Layout>
      <PhotoSwipe
        closePhotoSwipeCb={() => setImageSwipe({ ...imageSwipe, isOpen: false })}
        isOpen={imageSwipe.isOpen}
        items={imageSwipe.images}
        options={imageSwipe.swipeOptions}
      />
      <ModalSizeGuide
        department={department}
        product={product}
        setSizeGuideVisible={setSizeGuideVisible}
        sizeGuideVisible={sizeGuideVisible}
      />
      {isExcluded && <ModalProductExcluded product={product} />}
      <Breadcrumbs breadcrumbsList={breadcrumbList} gridArea={breadcrumbsArea} />
      <Thumbnails
        displayProductImage={displayProductImage}
        gridArea={thumbnailsArea}
        imagesAlt={imagesAlt}
        imageSwipe={imageSwipe}
        product={product}
      />
      <MainImage
        gridArea={mainImageArea}
        handleCarouselSlideChange={handleCarouselSlideChange}
        handlePhotoSwipeOpen={handlePhotoSwipeOpen}
        imageSwipe={imageSwipe}
        product={product}
      />
      <ProductActionCard
        addToCart={addToCart}
        department={department}
        formattedModelNumber={formattedModelNumber}
        gridArea={productActionCardArea}
        isLastOne={isLastOne}
        isOutOfStock={isOutOfStock}
        isUniversalSizeOnly={isUniversalSizeOnly}
        product={product}
        selectedSize={selectedSize}
        selectorOptions={selectorOptions}
        selectorTitleText={selectorTitleText}
        setSelectedSize={setSelectedSize}
        setSizeGuideVisible={setSizeGuideVisible}
      />
      <Tabs
        currentDesktopTab={currentDesktopTab}
        gridArea={tabsArea}
        setCurrentDesktopTab={setCurrentDesktopTab}
        tabs={productDescriptions.map(d => d.title)}
      />
      <ProductDescription
        currentDesktopTab={currentDesktopTab}
        data={productDescriptions}
        gridArea={productDescriptionArea}
      />
      <HelpfulInformation gridArea={helpfulInfoArea} localized={localized} />
      <RelatedProducts
        department={userFacingDepartment}
        gridArea={relatedProductsArea}
        product={product}
        products={relatedProducts}
      />
      <ProductMicrodata
        countryCode={countryCode}
        currencyIso={billingCurrencyCode}
        freeShipping={freeShipping}
        image={images ? images[0].medium : null}
        isOutOfStock={isOutOfStock || isExcluded}
        price={formatPrice(product.price.reducedAfterTax)}
        productName={model}
      />
    </Layout>
  );
};
